/**
 * Fleet Portal API service
 */
import { getNexusToken, clearNexusToken } from '@/utils/authToken';

const API_BASE = import.meta.env.VITE_API_URL || '';

function getToken(): string | null { return getNexusToken() || null; }
export function setToken(t: string) { localStorage.setItem('nexus_token', t); }
export function clearToken() { clearNexusToken(); }

async function fleetFetch<T = unknown>(path: string, opts: RequestInit = {}): Promise<T> {
  const token = getToken();
  if (!token) throw new Error('No autorizado');
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...opts.headers },
  });
  const data = await res.json();
  if (res.status === 401) { clearToken(); window.location.href = '/app/login'; throw new Error('Sesión expirada'); }
  if (!res.ok) throw new Error(data.message || data.error || `Error ${res.status}`);
  return data.data ?? data;
}

export async function fleetLogin(email: string, password: string) {
  const res = await fetch(`${API_BASE}/api/fleet/login`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || 'Error de autenticación');
  return data.data;
}

export async function fleetSignup(info: { company_name: string; rfc?: string; contact_name: string; email: string; phone?: string; password: string }) {
  const res = await fetch(`${API_BASE}/api/fleet/login`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'signup', ...info }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || 'Error de registro');
  return data.data;
}

export const getFleetDashboard = () => fleetFetch('/api/fleet/dashboard');

export const getFleetTrips = (params: Record<string, unknown> = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') qs.set(k, String(v)); });
  return fleetFetch(`/api/fleet/trips?${qs}`);
};
export const getFleetTripDetail = (id: string) => fleetFetch(`/api/fleet/trips?id=${id}`);

export const getFleetDrivers = (params: Record<string, unknown> = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') qs.set(k, String(v)); });
  return fleetFetch(`/api/fleet/drivers?${qs}`);
};
export const getFleetDriverDetail = (id: string) => fleetFetch(`/api/fleet/drivers?id=${id}`);
export const createFleetDriver = (d: Record<string, unknown>) => fleetFetch('/api/fleet/drivers', { method: 'POST', body: JSON.stringify(d) });
export const updateFleetDriver = (id: string, d: Record<string, unknown>) => fleetFetch(`/api/fleet/drivers?id=${id}`, { method: 'PUT', body: JSON.stringify(d) });
export const toggleFleetDriver = (id: string, is_active: boolean) => fleetFetch(`/api/fleet/drivers?id=${id}`, { method: 'PATCH', body: JSON.stringify({ is_active }) });

export const getFleetVehicles = (params: Record<string, unknown> = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') qs.set(k, String(v)); });
  return fleetFetch(`/api/fleet/vehicles?${qs}`);
};
export const getFleetVehicleDetail = (id: string) => fleetFetch(`/api/fleet/vehicles?id=${id}`);
export const createFleetVehicle = (d: Record<string, unknown>) => fleetFetch('/api/fleet/vehicles', { method: 'POST', body: JSON.stringify(d) });
export const updateFleetVehicle = (id: string, d: Record<string, unknown>) => fleetFetch(`/api/fleet/vehicles?id=${id}`, { method: 'PUT', body: JSON.stringify(d) });

export const getFleetDocuments = () => fleetFetch('/api/fleet/documents');

// Billing / invoicing (fleet-side)
export const getFleetAvailableRoutes = () => fleetFetch('/api/fleet/trips?status=completed&cxp_status=pending');
export const fleetStampInvoice = (routeIds: string[]) => fleetFetch('/api/fleet/stamp', { method: 'POST', body: JSON.stringify({ route_ids: routeIds }) });
